import { ScaleElement } from '@scale/base-element';
import { CartAPI } from '@scale/cart-api';
import { Events, VariantSelectedEvent } from '@scale/events';
import { formatMoney } from '@scale/currency';
import { getCartSections } from '@scale/utilities';
import { mergeItemsById } from '@scale/bundle-utils';

const HAS_BUNDLE = '[data-bundle-contributor]';

class ProductForm extends ScaleElement {
  #submitting = false;
  #stickyObserver;
  #currentVariant;

  setup() {
    const form = this.querySelector('form');
    if (form) form.addEventListener('submit', (e) => this.#handleSubmit(e), { signal: this.signal });

    this.#currentVariant = this.#initialVariant();

    this.onDocument(Events.VARIANT_SELECTED, (e) => this.#onVariantSelected(e.variant));
    this.onDocument(Events.BUNDLE_UPDATED, () => this.#refreshAtcPrice());
    this.onDocument(Events.QUANTITY_BREAKS_UPDATED, () => this.#refreshAtcPrice());

    this.#setupStickyATC();
    this.#setupNotifyForm();

    // Reflect the bundle total on first paint, once sibling bundle elements
    // have run their own setup and populated their item datasets.
    requestAnimationFrame(() => this.#refreshAtcPrice());
  }

  /** Variant matching the form's initial `id` input, falling back to the first variant. */
  #initialVariant() {
    const variants = this.#variants();
    const id = this.ref('variant-id')?.value;
    return variants.find((v) => String(v.id) === String(id)) ?? variants[0] ?? null;
  }

  #variants() {
    const json = this.querySelector('[data-product-variants], [data-ref="variants-json"]');
    if (!json) return [];
    try {
      return JSON.parse(json.textContent);
    } catch {
      return [];
    }
  }

  async #handleSubmit(e) {
    e.preventDefault();
    if (this.#submitting) return;

    const form = e.target;
    const errorEl = this.ref('error');
    const errorMsg = this.ref('error-message');
    if (errorEl) errorEl.hidden = true;

    const variantInput = this.ref('variant-id');
    if (!variantInput || variantInput.disabled) return;
    if (this.querySelector('[data-bundle-contributor][data-bundle-blocked="true"]')) return;

    const formData = new FormData(form);
    let items = [{
      id: Number(formData.get('id')),
      quantity: Number(formData.get('quantity')) || 1,
      properties: this.#extractProperties(formData),
    }];

    const sellingPlan = formData.get('selling_plan');
    if (sellingPlan) items[0].selling_plan = Number(sellingPlan);

    items = this.#applyBundleContributions(items);

    this.#submitting = true;
    const addButton = this.ref('add-to-cart');
    if (addButton) addButton.setAttribute('aria-busy', 'true');

    const text = this.ref('text');
    const loading = this.ref('loading');
    const check = this.ref('check');
    const status = this.ref('status');

    if (text) text.hidden = true;
    if (loading) loading.hidden = false;

    try {
      const cart = await CartAPI.add(items, {
        sections: getCartSections(),
      });

      if (loading) loading.hidden = true;
      if (check) check.hidden = false;
      if (status) status.textContent = window.Scale?.translations?.addedToCart || 'Added to cart';
      this.announce(window.Scale?.translations?.addedToCart || 'Added to cart');

      setTimeout(() => {
        if (check) check.hidden = true;
        if (text) text.hidden = false;
      }, 1500);
    } catch (err) {
      if (loading) loading.hidden = true;
      if (text) text.hidden = false;
      if (errorEl && errorMsg) {
        errorMsg.textContent = err.message;
        errorEl.hidden = false;
      }
      if (status) status.textContent = err.message;
    } finally {
      this.#submitting = false;
      if (addButton) addButton.removeAttribute('aria-busy');
    }
  }

  #onVariantSelected(variant) {
    this.#currentVariant = variant;

    const addButton = this.ref('add-to-cart');
    const label = this.ref('atc-label');
    const variantInput = this.ref('variant-id');
    const status = this.ref('status');

    if (variantInput) {
      variantInput.value = variant.id;
      variantInput.disabled = !variant.available;
    }

    if (addButton) {
      addButton.disabled = !variant.available;
      addButton.dataset.variantAvailable = String(variant.available);
    }

    const labelText = variant.available
      ? window.Scale?.translations?.addToCart || 'Add to cart'
      : window.Scale?.translations?.soldOut || 'Sold out';

    if (label) label.textContent = labelText;

    if (status) {
      status.textContent = variant.available
        ? `${labelText} ${formatMoney(variant.price)}`
        : labelText;
    }

    this.#refreshAtcPrice();
    this.#toggleNotify(variant);
  }

  /**
   * Resolve the displayed total for the current variant. With bundle contributors
   * (quantity breaks, bundles) the total is the contribution total — replacing the
   * variant price when a contributor replaces it, otherwise added on top. The compare
   * total follows the same rule, falling back to the variant compare-at price.
   */
  #computeTotal() {
    const variant = this.#currentVariant;
    if (!variant) return null;

    let total = variant.price;
    let compare = variant.compare_at_price || 0;

    for (const el of this.querySelectorAll(HAS_BUNDLE)) {
      const contribution = el.getContributionTotal?.();
      if (!contribution) continue;
      const contribCompare = contribution.compare ?? contribution.total;
      if (contribution.replace) {
        total = contribution.total;
        compare = contribCompare;
      } else {
        total += contribution.total;
        compare += contribCompare;
      }
    }

    return { total, compare };
  }

  /**
   * Set the ATC button price label (variant or discounted bundle total).
   */
  #refreshAtcPrice() {
    const atcPrice = this.ref('atc-price');
    const variant = this.#currentVariant;
    if (!atcPrice || !variant) return;

    if (!variant.available) {
      atcPrice.textContent = '';
      return;
    }

    const { total } = this.#computeTotal();
    atcPrice.textContent = `— ${formatMoney(total)}`;

    this.#updateStickyATC(variant);
  }

  /**
   * Gather getCartContribution() results from [data-bundle-contributor] descendants.
   * A contribution with replace=true overrides the form's main item (preserving form-level
   * properties and selling_plan on contributed items).
   */
  #applyBundleContributions(items) {
    const contributors = this.querySelectorAll('[data-bundle-contributor]');
    if (!contributors.length) return items;

    const baseProps = items[0]?.properties;
    const baseSellingPlan = items[0]?.selling_plan;
    let result = [...items];

    for (const el of contributors) {
      const contrib = el.getCartContribution?.();
      if (!contrib?.items?.length) continue;

      if (contrib.replace) {
        result = contrib.items.map((it) => ({
          ...it,
          properties: baseProps ? { ...baseProps, ...(it.properties ?? {}) } : it.properties,
          selling_plan: it.selling_plan ?? baseSellingPlan,
        }));
      } else {
        result.push(...contrib.items);
      }
    }

    return mergeItemsById(result);
  }

  #extractProperties(formData) {
    const props = {};
    for (const [key, value] of formData.entries()) {
      if (key.startsWith('properties[') && value) {
        props[key.replace('properties[', '').replace(']', '')] = value;
      }
    }
    return Object.keys(props).length ? props : undefined;
  }

  // --- Sticky ATC ---

  #isOvaPreview() {
    return document.documentElement.dataset.ovaPreview === 'true';
  }

  #setupStickyATC() {
    const stickyBar = this.ref('sticky-bar');
    const addButton = this.ref('add-to-cart');
    if (!stickyBar || !addButton) return;

    this.onDocument('ova:layout-mutated', () => this.#syncStickyAfterLayout());

    if (this.#isOvaPreview()) {
      this.#hideStickyBar(stickyBar);
      this.#bindStickyControls();
      return;
    }

    stickyBar.hidden = false;
    this.#bindStickyObserver(stickyBar, addButton);
    this.#bindStickyControls();
  }

  #hideStickyBar(stickyBar) {
    stickyBar.classList.remove('is-visible');
    stickyBar.hidden = true;
    this.#stickyObserver?.disconnect();
    this.#stickyObserver = undefined;
  }

  #syncStickyAfterLayout() {
    const stickyBar = this.ref('sticky-bar');
    const addButton = this.ref('add-to-cart');
    if (!stickyBar || !addButton) return;

    if (this.#isOvaPreview()) {
      this.#hideStickyBar(stickyBar);
      return;
    }

    stickyBar.classList.remove('is-visible');
    this.#stickyObserver?.disconnect();
    this.#bindStickyObserver(stickyBar, addButton);
  }

  #bindStickyObserver(stickyBar, addButton) {
    this.#stickyObserver = new IntersectionObserver(
      ([entry]) => {
        // Only show sticky when scrolled BELOW the button (button is above viewport)
        const isAboveViewport = entry.boundingClientRect.bottom < 0;
        stickyBar.classList.toggle('is-visible', !entry.isIntersecting && isAboveViewport);
      },
      { threshold: 0 }
    );
    this.#stickyObserver.observe(addButton);
  }

  #bindStickyControls() {
    const stickySubmit = this.ref('sticky-submit');
    if (stickySubmit) {
      stickySubmit.addEventListener('click', () => {
        const form = this.querySelector('form');
        if (form) form.requestSubmit();
      }, { signal: this.signal });
    }

    const stickyVariantSelect = this.ref('sticky-variant-select');
    if (stickyVariantSelect) {
      stickyVariantSelect.addEventListener('change', (e) => {
        const variantId = e.target.value;
        const variantInput = this.ref('variant-id');
        if (variantInput) variantInput.value = variantId;

        const variant = this.#variants().find((v) => String(v.id) === String(variantId));
        if (variant) this.broadcast(new VariantSelectedEvent(variant));
      }, { signal: this.signal });
    }

    const quantityInput = this.querySelector('input[name="quantity"]');
    if (quantityInput) {
      quantityInput.addEventListener('input', () => {
        if (this.#currentVariant) this.#updateStickyATC(this.#currentVariant);
      }, { signal: this.signal });
    }
  }

  /**
   * Mirror the ATC button state onto the sticky bar: variant title, the resolved
   * total (variant or quantity-break/bundle total), compare-at, quantity, availability.
   */
  #updateStickyATC(variant) {
    const stickyBar = this.ref('sticky-bar');
    if (!stickyBar) return;

    const { total, compare } = this.#computeTotal();

    const stickyVariant = this.ref('sticky-variant');
    if (stickyVariant) stickyVariant.textContent = variant.title;

    const stickyCurrentPrice = this.ref('sticky-current-price');
    if (stickyCurrentPrice) stickyCurrentPrice.textContent = formatMoney(total);

    const stickyVariantSelect = this.ref('sticky-variant-select');
    if (stickyVariantSelect) stickyVariantSelect.value = String(variant.id);

    const stickyCompare = this.ref('sticky-compare');
    if (stickyCompare) {
      const hasCompare = compare > total;
      stickyCompare.textContent = hasCompare ? formatMoney(compare) : '';
      stickyCompare.hidden = !hasCompare;
    }

    const stickyQuantity = this.ref('sticky-quantity');
    if (stickyQuantity) {
      const quantity = Number(this.querySelector('input[name="quantity"]')?.value) || 1;
      stickyQuantity.textContent = quantity;
      stickyQuantity.hidden = quantity <= 1;
    }

    const stickySubmit = this.ref('sticky-submit');
    if (stickySubmit) {
      stickySubmit.disabled = !variant.available;
      stickySubmit.dataset.variantAvailable = String(variant.available);
    }
  }

  // --- Notify form ---

  #setupNotifyForm() {
    const notifyForm = this.ref('notify-form');
    if (!notifyForm) return;

    const submitBtn = this.ref('notify-submit');
    const emailInput = this.ref('notify-email');

    submitBtn?.addEventListener('click', () => this.#handleNotifySubmit(), { signal: this.signal });
    emailInput?.addEventListener('keydown', (e) => {
      if (e.key === 'Enter') {
        e.preventDefault();
        this.#handleNotifySubmit();
      }
    }, { signal: this.signal });
  }

  #toggleNotify(variant) {
    const atcWrapper = this.ref('atc-wrapper');
    const notifyWrapper = this.ref('notify-wrapper');
    if (!notifyWrapper) return;

    const soldOut = !variant.available;
    if (atcWrapper) atcWrapper.hidden = soldOut;
    notifyWrapper.hidden = !soldOut;

    // Update variant ID in notify form
    const notifyVariantInput = this.ref('notify-variant-id');
    if (notifyVariantInput) notifyVariantInput.value = variant.id;
  }

  async #handleNotifySubmit() {
    const email = this.ref('notify-email')?.value;
    if (!email) return;

    const variantId = this.ref('notify-variant-id')?.value;
    const btn = this.ref('notify-submit');
    const form = this.ref('notify-form');
    if (btn) btn.setAttribute('aria-busy', 'true');

    try {
      await fetch(`${window.Shopify?.routes?.root || '/'}contact`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: new URLSearchParams({
          'form_type': 'customer',
          'contact[email]': email,
          'contact[tags]': 'back-in-stock',
          'contact[note]': `Variant: ${variantId}`,
        }),
      });

      const success = this.ref('notify-success');
      if (success) success.hidden = false;
      if (form) form.hidden = true;
    } catch {
      this.announce('An error occurred', 'assertive');
    } finally {
      if (btn) btn.removeAttribute('aria-busy');
    }
  }

  teardown() {
    this.#stickyObserver?.disconnect();
  }

}

if (!customElements.get('product-form')) {
  customElements.define('product-form', ProductForm);
}
